import type { View } from "./generated/views";

import * as z from "zod";

import { INTEGRATION_CONNECTED_CHECK_NAMES } from "./integrationWorkflowKeys";

/**
 * The onboarding checklist's vocabulary, read end to end.
 *
 * The state home renders, the check names a step finishes on, where a step's
 * work happens, what each app calls itself, and the tag that remembers a check
 * is done. They were five files in a chain, each importing the one before it.
 */

/**
 * The names a setup step may reference to say when it is finished.
 *
 * The implementations live in bedrock (`services/setup-checks.ts`); only the
 * names live here, because two things that cannot import each other both need
 * them: the plan-file generator, which rejects an unknown name at build time,
 * and the client, which reads the results back.
 */
export const SETUP_CHECK_NAMES = [
  "home-product",
  "home-page",
  "home-connection",
  "home-funnel",
  "home-storefront",
  "home-research",
  "home-ad-hooks",
  "home-products",
  "has-brand-context",
  "brand-business",
  "connect-store",
  "first-integration",
  "connect-slack",
  "has-connected-shopify",
  "has-connected-tool",
  "custom-domain",
  "has-published",
  "has-connected-assistant",
  "assistant-connected-claude",
  "assistant-connected-chatgpt",
  "connect-assistant",
  "has-insights-report",
  "has-project-skill",
  "has-scheduled-task",
  "growth-workflow",
  ...INTEGRATION_CONNECTED_CHECK_NAMES,
] as const;

export const GROWTH_WORKFLOW_CHECK_NAMES = [
  "has-insights-report",
  "has-project-skill",
  "has-scheduled-task",
] as const satisfies readonly (typeof SETUP_CHECK_NAMES)[number][];

export const setupCheckNameSchema = z
  .enum(SETUP_CHECK_NAMES)
  .describe("SetupCheckName");

export type SetupCheckName = (typeof SETUP_CHECK_NAMES)[number];

export const setupCheckResultSchema = z
  .object({
    complete: z.boolean(),
    current: z.number().int().nonnegative().optional(),
    target: z.number().int().positive().optional(),
  })
  .refine(
    ({ current, target }) => {
      return (
        (current === undefined && target === undefined) ||
        (current !== undefined && target !== undefined)
      );
    },
    {
      message: "Setup check progress requires both current and target",
    },
  )
  .describe("SetupCheckResult");

export type SetupCheckResult = z.infer<typeof setupCheckResultSchema>;

export function isSetupCheckName(value: string): value is SetupCheckName {
  return setupCheckNameSchema.safeParse(value).success;
}

/**
 * Every mini-app a tutorial can send someone to, and the name that app calls
 * itself. Shared so the agent's home section and the Tutorials app label the
 * same destination identically, and exhaustive so adding an app forces a
 * decision about what a CTA pointing at it should read.
 */
export const MINIAPP_NAMES = {
  assets: "Assets",
  "brand-studio": "Branding",
  content: "Content",
  draw: "Draw",
  files: "Files",
  insights: "Insights",
  integrations: "Integrations",
  orders: "Orders",
  plans: "Plans",
  products: "Products",
  settings: "Settings",
  skills: "Skills",
  tasks: "Tasks",
  "website-builder": "Site Builder",
} as const;

export type MiniappName = keyof typeof MINIAPP_NAMES;

/**
 * Where a tutorial's work actually happens, when it is somewhere other than
 * chat. Keyed by the `destination` / `destinationDone` fields on a tutorial's
 * plan metadata, and resolved to a typed View so every surface opens the same
 * place: the Tutorials app hands it to `openMiniappView`, the home section to
 * `getTabFromView`.
 *
 * Each names the app it lands in, so surfaces can say where a step's work
 * happens rather than describing the step twice.
 */
export const TUTORIAL_DESTINATIONS = {
  "brand-create": {
    app: "brand-studio",
    view: { viewId: "brandStudio.home", args: {} },
  },
  "brand-view": {
    app: "brand-studio",
    view: { viewId: "brandStudio.project", args: {} },
  },
  integrations: {
    app: "integrations",
    view: { viewId: "integrations.home", args: {} },
  },
  "shopify-connect": {
    app: "integrations",
    view: {
      viewId: "integrations.agentDetail",
      args: { integrationKey: "shopify" },
    },
  },
  "slack-connect": {
    app: "integrations",
    view: {
      viewId: "integrations.agentDetail",
      args: { integrationKey: "slack" },
    },
  },
  "custom-domain": {
    app: "website-builder",
    view: { viewId: "siteBuilder.domains", args: {} },
  },
  publish: {
    app: "website-builder",
    view: { viewId: "siteBuilder.dashboard", args: {} },
  },
  products: {
    app: "products",
    view: { viewId: "products.home", args: {} },
  },
  insights: {
    app: "insights",
    view: { viewId: "insights.home", args: {} },
  },
  skills: {
    app: "skills",
    view: { viewId: "skills.home", args: {} },
  },
  tasks: {
    app: "tasks",
    view: { viewId: "tasks.home", args: {} },
  },
} satisfies Record<string, { app: MiniappName; view: View }>;

export type TutorialDestination = keyof typeof TUTORIAL_DESTINATIONS;

export function isTutorialDestination(
  value: string,
): value is TutorialDestination {
  return Object.hasOwn(TUTORIAL_DESTINATIONS, value);
}

/**
 * Which side of a two-sided tutorial to open. A finished step falls back to its
 * unfinished destination, and vice versa, so declaring only one still works.
 */
export function resolveDestination({
  destination,
  destinationDone,
  done,
}: {
  destination: TutorialDestination | null;
  destinationDone: TutorialDestination | null;
  done: boolean;
}): TutorialDestination | null {
  if (done) {
    return destinationDone ?? destination;
  }
  return destination;
}

/**
 * Durable per-user milestones, on the generic `user_tag` primitive.
 *
 * The table is deliberately generic, but the names are not: they are built here
 * so a typo cannot quietly create a milestone that nothing will ever read.
 */

export function setupCheckDoneTagName({
  projectId,
  checkName,
}: {
  projectId: string;
  checkName: SetupCheckName;
}) {
  const version = checkName === "brand-business" ? ".v2" : "";
  return `agent.setup.${projectId}.${checkName}${version}.done`;
}

/**
 * Recorded when the user dismisses or finishes the first-run walkthrough.
 * No project segment: the tour teaches the product, so once is once
 * everywhere.
 */
export function firstRunTourSeenTagName() {
  return "agent.setup.first-run-tour.seen";
}
